<template>
  <div>
    <v-container >
      <CRUDCriteria
        :showSearch="false"
        :showReset="true"
      >
        <template v-slot:criteriaRows>
          <v-row>
            <v-col cols="2">
              <v-select class="tax-unit-criteria-by-fiscal-year-select"
                v-model.number="criteriaState.fiscalYear"
                :items="sortedFiscalYearRange"
                label="By Fiscal Year"
                @input="searchByCriteria"
              ></v-select>
            </v-col>
            <v-col cols="5">
              <v-btn
                class="ml-3"
                color="primary"
                small
                dense
                @click="validateRecords"
              >
                Validate Records
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </CRUDCriteria>
      <CRUDList
        :canAdd="canAddToSharedList"
        :canEdit="true"
        :canSave="canEditSharedList"
        :headers="headers"
        :baseCrudKey="baseCrudKey"
        :createNewItem="newItemDialog"
        toolbarTitle="Tax Unit"
        :toolbarContent="stringifyitem"
        @loadCRUDList="handleLoadCRUDList"
        @upsertItem="upsrtTransaction"
        :defaultSortBy="sortBy"
        :loadingData="gridLoading.flag"
        :loadingText="gridLoading.text"
      >
        <template v-slot:[`editedItem`]="{}">
          <taxUnitEditedItem
            :baseCrudKey="baseCrudKey"
          />
        </template>
      </CRUDList>
    </v-container>
    <v-dialog v-model="isRequiredToShowErrorDialog" max-width="600px">
        <v-card>
          <v-card-text class="pt-4">
            <v-alert type="error" outlined>
              <strong>Validation Errors</strong>
            </v-alert>
            <div v-if="validationErrorData.length">
              <h4>
                Record Details ({{ currentErrorIndex + 1 }} of {{ validationErrorData.length }}):
              </h4>
              <v-simple-table dense>
                <tbody>
                    <tr>
                      {{ validationErrorData[currentErrorIndex].message }}
                    </tr>
                  <tr>
                    <td class="error--text"
                      v-html="Array.isArray(validationErrorData[currentErrorIndex].values)
                        ? validationErrorData[currentErrorIndex].values.join('<br>')
                        : (typeof validationErrorData[currentErrorIndex].values === 'string'
                          ? validationErrorData[currentErrorIndex].values
                          .replace(/\n/g, '<br>')
                          .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
                          : validationErrorData[currentErrorIndex].values)"></td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              v-if="currentErrorIndex < validationErrorData.length - 1"
              color="primary"
              text
              @click="showNextError"
            >
              <v-icon left>mdi-arrow-right</v-icon>
              Continue ({{ currentErrorIndex + 1 }}/{{ validationErrorData.length }})
            </v-btn>
            <v-btn
              v-else
              color="primary"
              text
              disabled
            >
              <v-icon left>mdi-check</v-icon>
              Continue
            </v-btn>
            <v-btn color="error" text @click="hideErrorWindow">
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  </div>
</template>

<script>
import {
  mapGetters,
  mapActions,
  mapMutations,
  mapState,
} from 'vuex';
import CRUDList from '../../common/base/crud/CRUDList.vue';
import CRUDCriteria from '../../common/base/crud/CRUDCriteria.vue';
import taxUnitEditedItem from './taxUnitEditedItem.vue';
import {
  getFYList,
  toFiscalYear,
} from '../../../util/shared/tmc-global';
import { defaultGridLoading, manageGridLoading, amountFormat } from '../../../util/shared/vue-global';
import SharedListAccessControlMixin from '../SharedListAccessControl.mixin';

const getHeaders = () => {
  const headers = [
    { text: 'Section', value: 'appSection' },
    { text: '#', value: 'orderNumber' },
    { text: 'Description', value: 'description' },
    { text: 'General Line At', value: 'generalLineAt' },
    { text: 'Sub Section', value: 'subSection' },
    { text: 'Link To Unit Apportionment', value: 'toUnitDescForAppro' },
    { text: 'ADA/ABT', value: 'averageDailyAttendence' },
    { text: 'MV', value: 'motorVehicle' },
  ];
  return headers;
};

export default {
  name: 'taxUnitList',
  mixins: [SharedListAccessControlMixin],
  components: {
    CRUDCriteria,
    CRUDList,
    taxUnitEditedItem,
  },
  data: () => ({
    baseCrudKey: 'sharedTaxUnit',
    headers: [],
    gridLoading: defaultGridLoading(),
    sortBy: [
      {
        column: 'appSection',
        direction: 'asc',
      },
      {
        column: 'orderNumber',
        direction: 'asc',
      },
    ],
    isRequiredToShowErrorDialog: false,
    validationErrorData: [],
    currentErrorIndex: 0,
  }),
  created() {
    this.headers = getHeaders();
    this.initCriteria();
    this.criteriaState.fiscalYear = toFiscalYear(new Date(this.todaysDate));
    this.loadFiscalRegYears();
  },
  computed: {
    ...mapGetters('base/crud', [
      'criteria',
      'index',
      'item',
    ]),
    ...mapGetters(['todaysDate']),
    ...mapState({
      fiscalYearRange: (state) => {
        const registerdYears = state.shared.taxUnit.regYears || {};
        const fyList = getFYList(registerdYears);
        return fyList.sort((a, b) => b - a);
      },
    }),
    sortedFiscalYearRange() {
      return [...(this.fiscalYearRange || [])].sort((a, b) => b - a);
    },
    criteriaState: {
      get() {
        return this.criteria(this.baseCrudKey);
      },
      set(value) {
        this.setCriteria([this.baseCrudKey, value]);
      },
    },
    totals() {
      const items = this.$store.state.base.crud.items[this.baseCrudKey] || [];

      let totalADA = 0;
      let totalABT = 0;
      let totalMV = 0;

      items.forEach((item) => {
        if (item.appSection === 'B') {
          totalABT += item.averageDailyAttendence || 0;
        } else {
          totalADA += item.averageDailyAttendence || 0;
        }
        totalMV += item.motorVehicle || 0;
      });

      return { totalADA, totalABT, totalMV };
    },

    stringifyitem() {
      return `
        <span style="margin-right: 40px;">Totals</span>
        <span style="margin-right: 40px;">ADA:  ${amountFormat(this.totals.totalADA)}</span>
        <span style="margin-right: 40px;">ABT:  ${amountFormat(this.totals.totalABT)}</span>
        <span>MV:  ${amountFormat(this.totals.totalMV)}</span>
      `;
    },
  },
  methods: {
    ...mapActions(['flashInfo', 'flashSuccess']),
    ...mapActions('shared/taxUnit', [
      'loadTaxUnit',
      'upsertTaxUnit',
      'loadFiscalRegYears',
      'validateTaxUnit',
    ]),
    ...mapMutations('base/crud', [
      'setCriteria',
    ]),
    handleLoadCRUDList() {
      const { baseCrudKey } = this;
      const criteria = this.criteriaState;
      manageGridLoading(this, true, 'Searching...');
      this.loadTaxUnit({
        criteria,
        baseCrudKey,
      }).finally(() => {
        manageGridLoading(this, false);
      });
    },
    async upsrtTransaction(item) {
      const { baseCrudKey } = this;
      const updatedItem = { ...item };
      if (updatedItem.appSection !== 'B') {
        updatedItem.motorVehicle = null;
      }
      if (updatedItem.orderNumber === '') {
        updatedItem.orderNumber = null;
      }
      await this.upsertTaxUnit({ item: updatedItem, baseCrudKey });
    },
    newItemDialog() {
      let newItem = {};
      newItem = {
        fiscalYear: this.criteriaState.fiscalYear,
        orderNumber: '',
        description: '',
        averageDailyAttendence: 0,
        motorVehicle: 0,
        details: [{
          fundDescription: '',
          appFund: '',
          sharedFundAltKey: '',
          linkToMisc: '',
          remitTo: '',
        }],
      };
      return newItem;
    },
    initCriteria() {
      // reset criteria while merge flag is true
      this.criteriaState = {};
    },
    searchByCriteria() {
      this.handleLoadCRUDList();
    },
    async validateRecords() {
      if (this.criteriaState.fiscalYear) {
        if (window.confirm('Are you sure you want to validate these items?')) {
          const initialCriteria = {
            fiscalYear: this.criteriaState.fiscalYear,
          };
          try {
            const validationResult = await this.validateTaxUnit({ criteria: initialCriteria });
            if (validationResult && validationResult.isValid === 'false') {
              // Access each message and its values
              this.validationErrorData = validationResult.messages || [];
              this.currentErrorIndex = 0;
              this.isRequiredToShowErrorDialog = true;
            } else {
              this.flashSuccess('All records are valid!');
            }
          } catch (e) {
            alert(`Validation failed:  ${e.message || e}`);
          }
        }
      } else {
        this.flashInfo('Please select Fiscal year!');
      }
    },
    showNextError() {
      if (this.currentErrorIndex < this.validationErrorData.length - 1) {
        this.currentErrorIndex += 1;
      }
    },
    hideErrorWindow() {
      this.currentErrorIndex = 0;
      this.validationErrorData = [];
      this.isRequiredToShowErrorDialog = false;
      this.handleLoadCRUDList();
    },
  },
};

</script>
